import React, { useEffect, useRef, useState } from "react";

import PillarRegistryPanel from "./PillarRegistryPanel";
import { VOICE_LANGS, createRecognizer, isSTTSupported } from "../../voice";


function isVanMessage(message) {
  return String(message?.author_user_id || "").toLowerCase().startsWith("vanhelsin");
}


function formatCreatedAt(epoch) {
  const value = Number(epoch || 0);
  if (!value) return "";
  try {
    return new Date(value * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}


export default function ChatRoomView({
  room,
  messages,
  badges,
  busy,
  error,
  currentUserId,
  onSend,
  onAnalyze,
  onOpenInvite,
  onOpenJudgement,
  onVoicePauseMusic,
  onVoiceResumeMusic,
}) {
  const [draft, setDraft] = useState("");
  const [voiceLang, setVoiceLang] = useState(() => {
    try {
      return String(sessionStorage.getItem("vh_chatroom_voice_lang") || "pt-BR");
    } catch {
      return "pt-BR";
    }
  });
  const [isRecording, setIsRecording] = useState(false);
  const [interimVoiceText, setInterimVoiceText] = useState("");
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognizerRef = useRef(null);
  const roomTitle = String(room?.room?.title || room?.title || "Sala").trim();
  const latestAnalysis = room?.latest_analysis || null;
  const participants = Array.isArray(room?.participants) ? room.participants : [];
  const sttSupported = typeof window !== "undefined" && isSTTSupported();

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.minHeight = "44px";
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, 44), 220);
    el.style.height = `${next}px`;
  }, [draft]);

  useEffect(() => {
    try {
      sessionStorage.setItem("vh_chatroom_voice_lang", voiceLang);
    } catch {}
  }, [voiceLang]);

  useEffect(() => {
    return () => {
      const rec = recognizerRef.current;
      recognizerRef.current = null;
      if (rec) {
        try {
          rec.stop();
        } catch {}
      }
    };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    const text = String(draft || "").trim();
    if (!text) return;
    await onSend?.(text);
    setDraft("");
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  }

  function stopRecording() {
    const rec = recognizerRef.current;
    recognizerRef.current = null;
    setIsRecording(false);
    setInterimVoiceText("");
    if (rec) {
      try {
        rec.stop();
      } catch {}
    }
    onVoiceResumeMusic?.();
  }

  function startRecording() {
    if (!sttSupported) {
      window.alert("Reconhecimento de voz nao suportado neste dispositivo.");
      return;
    }
    if (isRecording) return;

    onVoicePauseMusic?.();
    setIsRecording(true);
    setInterimVoiceText("");

    const rec = createRecognizer({
      lang: voiceLang,
      onText: (text, isFinal) => {
        setInterimVoiceText(text);
        if (isFinal) {
          setDraft((prev) => {
            const base = String(prev || "");
            const spacer = base && !base.endsWith(" ") ? " " : "";
            return `${base}${spacer}${text}`.trim();
          });
        }
      },
      onError: () => {
        stopRecording();
      },
      onEnd: () => {
        stopRecording();
      },
    });

    if (!rec) {
      setIsRecording(false);
      window.alert("Nao foi possivel iniciar a transcricao de voz.");
      return;
    }

    recognizerRef.current = rec;
    try {
      rec.start();
    } catch {
      stopRecording();
    }
  }

  return (
    <div className="vh-chatrooms-room-shell">
      <div className="chat-layout vh-chatrooms-chat-layout">
        <div className="chat vh-chatrooms-chat">
          <div className="vh-ai-banner" role="status" aria-live="polite">
            Tribunal do Cacador isolado do modo normal. Use @vanhelsin moral | heroi | vampiro para chamar o parecer.
          </div>

          <div className="vh-chatrooms-room-banner">
            <div>
              <div className="vh-chatrooms-section-kicker">Sala ativa</div>
              <div className="vh-chatrooms-room-title">{roomTitle}</div>
              <div className="vh-chatrooms-room-meta">
                {participants.length || 0} participante(s) no Tribunal
              </div>
            </div>
            <div className="vh-chatrooms-room-actions">
              <button type="button" className="btn gothic-btn" onClick={onOpenInvite}>
                Convidar
              </button>
              <button type="button" className="btn gothic-btn" onClick={() => onAnalyze?.("moral")}>
                Moral
              </button>
              <button type="button" className="btn gothic-btn" onClick={() => onAnalyze?.("heroi")}>
                Heroi
              </button>
              <button type="button" className="btn gothic-btn" onClick={() => onAnalyze?.("vampiro")}>
                Vampiro
              </button>
            </div>
          </div>

          <PillarRegistryPanel badges={badges} />

          {latestAnalysis?.result ? (
            <div className="vh-chatrooms-analysis-banner">
              <div>
                <div className="vh-chatrooms-section-kicker">Ultimo veredito</div>
                <div className="vh-chatrooms-analysis-preview">
                  {String(latestAnalysis.result.result_text || "").slice(0, 320)}
                </div>
              </div>
              <button type="button" className="btn gothic-btn" onClick={onOpenJudgement}>
                Abrir veredito
              </button>
            </div>
          ) : null}

          {(Array.isArray(messages) ? messages : []).map((message) => {
            const authorId = String(message?.author_user_id || "").trim();
            const mine = currentUserId && authorId === currentUserId;
            const van = isVanMessage(message);
            const className = [
              "msg",
              van ? "ia-heroi vh-chatrooms-analysis-message" : mine ? "user-msg vh-chatrooms-own-message" : "vh-chatrooms-participant-message",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <div
                key={message.message_id || message.id}
                className={className}
                onDoubleClick={van ? onOpenJudgement : undefined}
                onKeyDown={
                  van
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onOpenJudgement?.();
                        }
                      }
                    : undefined
                }
                role={van ? "button" : undefined}
                tabIndex={van ? 0 : undefined}
              >
                <div className="vh-chatrooms-msg-head">
                  <strong>
                    {van
                      ? "Van Helsin"
                      : message.author_display_name_snapshot || message.display_name || "Participante"}
                  </strong>
                  <span>{formatCreatedAt(message.created_at)}</span>
                </div>
                <div className="vh-chatrooms-msg-text">{message.text || message.content}</div>
              </div>
            );
          })}

          {!messages?.length ? (
            <div className="vh-chatrooms-empty-state">
              A sala ainda esta em silencio. Conversem normalmente e chamem o Van quando quiserem um parecer.
            </div>
          ) : null}

          <div ref={chatEndRef} />
        </div>
      </div>

      <form className="input-bar vh-chatrooms-input-bar" onSubmit={handleSubmit}>
        <div className="voice-controls">
          <select value={voiceLang} onChange={(event) => setVoiceLang(event.target.value)} title="Idioma da voz">
            {VOICE_LANGS.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn gothic-btn"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!sttSupported}
            title={sttSupported ? (isRecording ? "Parar gravacao" : "Iniciar gravacao") : "Voz indisponivel"}
          >
            {isRecording ? "⏹️" : "🎤"}
          </button>
        </div>
        <textarea
          ref={inputRef}
          className="input-heroi vh-chatrooms-compose-textarea"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onClick={() => {
            const el = inputRef.current;
            if (!el) return;
            el.focus({ preventScroll: true });
            requestAnimationFrame(() => {
              el.scrollIntoView({ block: "center", behavior: "smooth" });
            });
          }}
          onTouchEnd={(event) => {
            event.stopPropagation();
            const el = inputRef.current;
            if (!el) return;
            el.focus({ preventScroll: true });
            requestAnimationFrame(() => {
              el.scrollIntoView({ block: "center", behavior: "smooth" });
            });
          }}
          placeholder="Escreva para a sala. Enter envia. Shift+Enter quebra linha. Use @vanhelsin para chamar o Tribunal."
          rows={1}
          enterKeyHint="send"
          inputMode="text"
          autoCapitalize="sentences"
          autoComplete="off"
        />
        <button type="submit" className="send-btn gothic-btn" disabled={busy}>
          {busy ? "Enviando..." : "Enviar"}
        </button>
      </form>
      {error ? <div className="vh-chatrooms-inline-error">{error}</div> : null}
      {interimVoiceText ? <div className="voice-interim">🎙️ {interimVoiceText}</div> : null}
    </div>
  );
}
